<template>
    <app-layout>
        <div class="ferpa-consent-page">
            <Header :title="$t('FERPA Consent to Release Student Information')"/>
            <b-container class="py-5">
                <b-row class="justify-content-center">
                    <b-col cols="12" lg="10">
                        <div class="bg-white p-4 p-md-5 rounded shadow-sm border">
                            <div v-if="isSubmitted" class="text-center py-5 bg-success text-white rounded">
                                <div class="mb-3">
                                    <i class="ri-checkbox-circle-line" style="font-size: 5rem;"></i>
                                </div>
                                <h2 class="font-weight-bold text-white">Success!</h2>
                                <p class="lead text-white">The form has been submitted</p>
                            </div>
                            <div v-else>
                                <div class="mb-4 text-center">
                                    <h2 class="font-weight-bold text-primary-dark">FERPA Consent to Release Student Information</h2>
                                    <p class="text-muted" v-if="studentType!=='mentora'">Office of the Registrar</p>
                                </div>

                                <b-alert show variant="info" class="small mb-4 border-0 bg-light-info text-dark">
                                    <div class="d-flex">
                                        <i class="ri-information-line ri-lg mr-3 mt-1"></i>
                                        <div>
                                            The Family Educational Rights and Privacy Act (FERPA), Public Law 93-380, Section 438 of the General Education
                                            Provision Act, is a federal law enacted in 1974 which affords students certain rights with respect to their education
                                            records. See the Grade Appeals & Grade Change policy in the Academic Catalog for the appeals process.<br><br>
                                            Students who wish to allow a third party to access the student’s academic records may do so by completing a FERPA
                                            Waiver Form. If a student owes outstanding fees of any kind to the University, all academic records will be withheld until
                                            the account is resolved.
                                            <br><br>
                                            <span v-if="studentType==='mentora'">
                                                Student records may include admission, registration, academic, financial and student account information.
                                                Students may give permission to release FERPA protected student information by completing and signing this form.
                                                Students may submit this form to an Administrative Officer in-person with a photo ID or via their BAU email account to   <a href="mailto:student_esl@bau.edu" class="font-weight-bold">student_esl@bau.edu</a> .
                                            </span>
                                            <span v-else>
                                                Student records may include admission, registration, academic, financial and student account information. Students may
                                                give permission to release FERPA protected student information by completing and signing this form. Students may
                                                submit this form to the Registrar in-person with a photo ID or via their BAU email account to <a href="mailto:registrar@bau.edu" class="font-weight-bold">registrar@bau.edu</a> .
                                            </span>
                                        </div>
                                    </div>
                                </b-alert>
                                <ValidationObserver ref="form">
                                        <section class="mb-5">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark">
                                                <i class="ri-user-3-line mr-2"></i>Student Information
                                            </h5>
                                            <b-row>
                                                <b-col md="6">
                                                    <b-form-group :label="$t('name')" label-class="font-weight-bold small">
                                                        <b-form-input v-model="userfullName" disabled class="bg-light"></b-form-input>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col md="6">
                                                    <b-form-group :label="$t('student_id')" label-class="font-weight-bold small">
                                                        <b-form-input v-model="studentId" disabled class="bg-light"></b-form-input>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                        </section>
                                        <!-- Information to be Released -->
                                        <section class="mb-5">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark">
                                                <i class="ri-file-list-3-line mr-2"></i>Information to be Released
                                            </h5>
                                            <ValidationProvider name="records_selected" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                                                <b-form-group :invalid-feedback="!hasSelectedRecords?'Please select at least one record type to release.':''" :state="hasSelectedRecords ? true : (errors.length > 0 ? false : null)">
                                                    <b-row>
                                                        <b-col md="6" class="mb-3">
                                                            <label class="font-weight-bold small text-uppercase text-secondary mb-3 d-block">Academic and Admission</label>
                                                            <b-form-checkbox v-model="form.release_academic" class="mb-2">
                                                                <span class="font-weight-bold">Academic:</span> <span class="small text-muted">grades/GPA, registration, progress, transcripts.</span>
                                                            </b-form-checkbox>
                                                            <b-form-checkbox v-model="form.release_academic_advising" class="mb-2">
                                                                <span class="font-weight-bold">Academic Advising:</span> <span class="small text-muted">conversations with faculty.</span>
                                                            </b-form-checkbox>
                                                            <b-form-checkbox v-model="form.release_admission" class="mb-2">
                                                                <span class="font-weight-bold">Admission:</span> <span class="small text-muted">records maintained by Office of Admissions.</span>
                                                            </b-form-checkbox>
                                                        </b-col>
                                                        <b-col md="6" class="mb-3">
                                                            <label class="font-weight-bold small text-uppercase text-secondary mb-3 d-block">Financial and Student Accounts</label>
                                                            <b-form-checkbox v-model="form.release_financial_aid" class="mb-2">
                                                                <span class="font-weight-bold">Financial Aid:</span> <span class="small text-muted">awards, disbursements, eligibility.</span>
                                                            </b-form-checkbox>
                                                            <b-form-checkbox v-model="form.release_student_accounts" class="mb-2">
                                                                <span class="font-weight-bold">Student Accounts:</span> <span class="small text-muted">billing, charges, credits, payments.</span>
                                                            </b-form-checkbox>

                                                            <div class="mt-3">
                                                                <ValidationProvider name="release_specific_records" rules="max:500" v-slot="{ errors }">
                                                                    <b-form-group :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                                        <label class="small font-weight-bold">Other Specific Records:</label>
                                                                        <b-form-textarea v-model="form.release_specific_records" placeholder="Specify any other records..." rows="2" :state="errors.length > 0 ? false : null"></b-form-textarea>
                                                                    </b-form-group>
                                                                </ValidationProvider>
                                                            </div>
                                                        </b-col>
                                                    </b-row>
                                                    <input type="hidden" v-model="hasSelectedRecords" />
                                                </b-form-group>
                                            </ValidationProvider>
                                        </section>
                                        <!-- Duration of Release -->
                                        <section class="mb-5 bg-light p-4 rounded border">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark">
                                                <i class="ri-calendar-event-line mr-2"></i>Duration of Release
                                            </h5>
                                            <ValidationProvider name="duration_type" rules="required" v-slot="{ errors }">
                                                <b-form-group :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                    <b-row>
                                                        <b-col md="4" class="mb-3 mb-md-0">
                                                            <b-form-radio v-model="form.duration_type" value="one_time" name="duration_type">One-Time Release</b-form-radio>
                                                        </b-col>
                                                        <b-col md="4" class="mb-3 mb-md-0 border-md-left border-md-right" v-if="studentType==='bau'">
                                                            <b-form-radio v-model="form.duration_type" value="semester" name="duration_type">{{semesterLabel}}</b-form-radio>
                                                            <div class="mt-2 pl-4" v-if="form.duration_type === 'semester'">
                                                                <ValidationProvider name="semester_id" rules="required" v-slot="{ errors: semesterErrors }">
                                                                    <b-form-group :invalid-feedback="semesterErrors[0]" :state="semesterErrors.length > 0 ? false : null" class="mb-0">
                                                                        <multi-selectbox
                                                                            v-model="form.semester_id" :multiple="false"
                                                                            :options="semesterOptions" :validate-error="errors[0]"
                                                                        ></multi-selectbox>
                                                                    </b-form-group>
                                                                </ValidationProvider>
                                                            </div>
                                                        </b-col>
                                                      <b-col md="4" class="mb-3 mb-md-0 border-md-left border-md-right" v-else-if="studentType==='mentora'">
                                                        <b-form-radio v-model="form.duration_type" value="module_date" name="duration_type">{{semesterLabel}}</b-form-radio>
                                                        <div class="mt-2 pl-4" v-if="form.duration_type === 'module_date'">
                                                          <ValidationProvider name="module_date_id" rules="required" v-slot="{ errors: semesterErrors }">
                                                            <b-form-group :invalid-feedback="semesterErrors[0]" :state="semesterErrors.length > 0 ? false : null" class="mb-0">
                                                              <multi-selectbox
                                                                  v-model="form.module_date_id" :multiple="false"
                                                                  :options="semesterOptions" :validate-error="errors[0]"
                                                              ></multi-selectbox>
                                                            </b-form-group>
                                                          </ValidationProvider>
                                                        </div>
                                                      </b-col>
                                                        <b-col md="4">
                                                            <b-form-radio v-model="form.duration_type" value="until_date" name="duration_type">Release Record Until</b-form-radio>
                                                            <div class="mt-2 pl-4" v-if="form.duration_type === 'until_date'">
                                                                <ValidationProvider name="until_date" :rules="`required`" v-slot="{ errors: dateErrors }">
                                                                    <b-form-group :invalid-feedback="dateErrors[0]" :state="dateErrors.length > 0 ? false : null" class="mb-0">
                                                                        <SelectDate v-model="form.until_date" :state="dateErrors.length > 0 ? false : null"/>
                                                                    </b-form-group>
                                                                </ValidationProvider>
                                                            </div>
                                                        </b-col>
                                                    </b-row>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </section>

                                        <!-- Security Passcode -->
                                        <section class="mb-5">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark">
                                                <i class="ri-lock-password-line mr-2"></i>Security Passcode
                                            </h5>
                                            <b-row>
                                                <b-col md="6">
                                                    <p class="small text-muted mb-2">Please designate a <strong>non-sequential 4-digit passcode</strong> for identity verification.</p>
                                                    <ValidationProvider name="passcode" rules="min:4" v-slot="{ errors }">
                                                        <b-form-group label="4-Digit Passcode" label-class="small font-weight-bold" :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <b-form-input v-model="form.passcode" maxlength="4" style="width: 150px" placeholder="****" :state="errors.length > 0 ? false : null"></b-form-input>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                </b-col>
                                            </b-row>
                                        </section>

                                        <!-- Third Party Designee -->
                                        <section class="mb-5">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark">
                                                <i class="ri-group-line mr-2"></i>Third Party Designee
                                            </h5>
                                            <b-row>
                                                <b-col md="6">
                                                    <ValidationProvider name="designee_name" rules="required|max:255" v-slot="{ errors }">
                                                        <b-form-group label="Full Name" label-class="small font-weight-bold" :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <b-form-input v-model="form.designee_name" placeholder="Recipient's Name" :state="errors.length > 0 ? false : null"></b-form-input>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                    <ValidationProvider name="designee_email" rules="required|email" v-slot="{ errors }">
                                                        <b-form-group label="Email Address" label-class="small font-weight-bold" :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <b-form-input type="email" v-model="form.designee_email" placeholder="email@example.com" :state="errors.length > 0 ? false : null"></b-form-input>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                    <ValidationProvider name="designee_phone" rules="required|max:20" v-slot="{ errors }">
                                                        <b-form-group label="Phone Number" label-class="small font-weight-bold" :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <phone-number-input v-model="form.designee_phone"/>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                </b-col>
                                                <b-col md="6">
                                                    <label class="small font-weight-bold">Relationship to Student</label>
                                                    <ValidationProvider name="designee_relationship" rules="required" v-slot="{ errors }">
                                                        <b-form-group :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <b-row class="small">
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="parents">Parents</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="friend">Friend</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="employer">Employer</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="legal_representative">Legal Rep.</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="sponsor">Sponsor</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="spouse">Spouse</b-form-radio></b-col>
                                                                <b-col cols="6" class="mb-2"><b-form-radio v-model="form.designee_relationship" value="other">Other</b-form-radio></b-col>
                                                            </b-row>
                                                        </b-form-group>
                                                    </ValidationProvider>

                                                    <ValidationProvider name="designee_relationship_other" :rules="form.designee_relationship === 'other' ? 'required|max:100' : ''" v-slot="{ errors }">
                                                        <b-form-group label="If 'Other', please specify:" label-class="small font-weight-bold" :invalid-feedback="errors[0]" :state="errors.length > 0 ? false : null">
                                                            <b-form-input v-model="form.designee_relationship_other" size="sm" placeholder="Specify relationship" :disabled="form.designee_relationship !== 'other'" :state="errors.length > 0 ? false : null"></b-form-input>
                                                        </b-form-group>
                                                    </ValidationProvider>
                                                </b-col>
                                            </b-row>
                                        </section>

                                        <hr class="my-5">

                                        <!-- Authorization -->
                                        <section class="mb-5">
                                            <h5 class="section-title border-bottom pb-2 mb-3 text-primary-dark font-weight-bold">
                                                STUDENT SIGNATURE
                                            </h5>

                                            <p class="mb-3">
                                                I, <span class="font-weight-bold">{{ userfullName }}</span>, authorize the release of the identified information from my education records to:
                                            </p>

                                            <p class="mb-3">
                                                <span class="font-weight-bold border-bottom border-dark px-3">{{ form.designee_name }}</span>
                                                <span class="small text-muted ml-2">(Name of person whom the student records will be released)</span>
                                            </p>

                                            <p class="mb-4">
                                                I understand the information may be released orally or in the form of copies of written records, as preferred by the requester.
                                                I understand I may revoke this Consent upon providing written notice to the University.
                                                I further understand that until this revocation is made, this consent shall remain in effect and my records will continue to be provided to
                                                <span class="font-weight-bold border-bottom border-dark px-3">{{ form.designee_name  }}</span>
                                                <span class="small text-muted ml-1">(Name of the person whom the records will be released).</span>
                                            </p>

                                            <b-row class="align-items-center mb-3">
                                                <b-col md="2" class="font-weight-bold">Signature:</b-col>
                                                <b-col md="10">
                                                    <ValidationProvider name="signature" rules="required" v-slot="{ errors }">
                                                        <SignatureComponent
                                                            v-model="form.signature"
                                                            ref="signatureComponentRef"
                                                        />
                                                    </ValidationProvider>
                                                </b-col>
                                            </b-row>

                                            <b-row class="align-items-center">
                                                <b-col md="2" class="font-weight-bold">Date:</b-col>
                                                <b-col md="4">
                                                    <b-form-input type="text" :value="currentDate" disabled class="bg-light"></b-form-input>
                                                </b-col>
                                            </b-row>
                                        </section>

                                        <div class="d-flex justify-content-end gap-3 pb-4">
                                            <processing-button @click="save" label="Save and Submit"
                                                               :processing="processing"></processing-button>
                                        </div>
                                </ValidationObserver>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </b-container>
        </div>
    </app-layout>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import moment from "moment";
import SignatureComponent from "@/components/elements/Signature.vue";
import ProcessingButton from "@/components/elements/ProcessingButton.vue";
import PhoneNumberInput from "@/components/elements/PhoneNumberInput.vue";
import ferpaConsentRealaseFormServices from "@/services/FerpaConsentRealaseFormServices";
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox.vue";
export default {
    components: {
        MultiSelectbox,
        PhoneNumberInput,
        ProcessingButton,
        SignatureComponent,
        ValidationProvider,
        ValidationObserver
    },
    metaInfo() {
        return {
            title: 'FERPA CONSENT TO RELEASE STUDENT INFORMATION'
        }
    },

        data() {
        return {
            isSubmitted: false,
            semesterOptions:[],
            processing:false,
            currentDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
            todayDate: moment().format('YYYY-MM-DD'),
            tomorrowDate: moment().add(1, 'days').format('YYYY-MM-DD'),
            form: {
                release_academic: false,
                release_academic_advising: false,
                release_admission: false,
                release_financial_aid: false,
                release_student_accounts: false,
                release_specific_records: '',
                module_date_id:null,
                duration_type: '',
                one_time: false,
                semester_id: null,
                until_date: '',
                passcode: '',
                designee_name: '',
                designee_email: '',
                designee_phone: '',
                designee_relationship: '',
                designee_relationship_other: '',
                signature: ''
            }
        }
    },
    computed:{
        userfullName() {
            const user =this.$store.getters["auth/getUser"];
            let name=null
            let surname=null
             if (user){
                 name=user.name
                 surname=user.surname
             }
            return  name+" "+surname
        },
        studentId(){
            const user =this.$store.getters["auth/getUser"];
            let id=null
            if (user){
                id=user.student?.student_number
            }
            return id
        },
        studentType(){
          const  user = this.$store.getters["auth/getUser"];
          let studentType=null
            if (user){
                studentType=user.active_student_program?.student_type
            }
            return studentType
        },
        hasSelectedRecords() {
            return this.form.release_academic ||
                   this.form.release_academic_advising ||
                   this.form.release_admission ||
                   this.form.release_financial_aid ||
                   this.form.release_student_accounts ||
                   (this.form.release_specific_records && this.form.release_specific_records.length > 0);
        },
        semesterLabel (){
         return  this.studentType==='mentora'?'Choose ONE Module':'Choose ONE Semester'
        }
    },
    watch: {
        'form.designee_relationship'(newVal){
            if (newVal!=='other'){
                this.form.designee_relationship_other=null
            }
        },
        'form.duration_type'(newVal) {
            this.form.one_time = false;
            delete this.form.until_date
            delete this.form.semester_id
            delete this.form.module_date_id

            if (newVal === 'one_time') {
                this.form.one_time = true;
            } else if (newVal === 'semester') {
                this.getSemesterDate()
            }
            else if (newVal === 'module_date') {
                this.getSemesterDate()
            }
            else if (newVal === 'until_date') {
                delete this.form.semester_id
            }
        }
    },
    methods: {
        async save() {
             const isValid =await this.$refs.form.validate()
             const isSignatureValid =this.$refs.signatureComponentRef.validate()
            if (!isValid && !isSignatureValid) return
            try {

                this.processing=true
                 await ferpaConsentRealaseFormServices.save(this.form)
                this.isSubmitted = true
                this.$toast.success(this.$t("The form has been submitted"));
            }catch (e){
                this.showErrors(e,this.$refs.form)
            }finally {
                this.processing=false
            }
        },
        async getSemesterDate(){
           try {
               const res=await ferpaConsentRealaseFormServices.getSemesters()
               const data =res.data.data
               this.setSemesterStatus(data)
           }catch (err){
               this.showErrors(err,null)
           }
        },
        setSemesterStatus(data){
            let selectedModules=[]
            if (this.studentType==='mentora'){
                selectedModules=data.available_modules
            }else{
                selectedModules=data.semesters
            }
            this.semesterOptions=selectedModules?.map((semester,key)=>{
                return {
                    text:semester.name,
                    value:semester.id
                }
            })
        }
    }
}
</script>

<style scoped>
.ferpa-consent-page {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.text-primary-dark {
    color: #1a3352;
}

.bg-light-info {
    background-color: #e3f2fd;
}

.section-title {
    font-weight: 700;
    display: flex;
    align-items: center;
}

.btn-submit {
    background-color: #1a3352;
    border-color: #1a3352;
}

.btn-submit:hover {
    background-color: #13263e;
    border-color: #13263e;
}

/* Responsive adjustments */
@media (min-width: 768px) {
    .border-md-left {
        border-left: 1px solid #dee2e6;
    }
    .border-md-right {
        border-right: 1px solid #dee2e6;
    }
}
</style>
